import { Link, useRouterState } from '@tanstack/react-router';
import { useUscita } from '../api/sessione.js';
import { t } from '../i18n/lingua.js';
import marchio from '../immagini/la-regina-san-marzano.webp';
import { type Sezione, usePuoAprire } from '../permessi.js';
import { Icona, type NomeIcona } from './Icone.js';

/**
 * Il menu, com'è oggi in Instant Developer: le sei voci delle anagrafiche
 * restano sei perché è così che gli utenti le cercano, anche se dietro sono
 * lo stesso elenco con un filtro diverso.
 *
 * Le voci ancora da costruire portano a una pagina che lo dice: preferisco
 * un menu completo con qualche porta chiusa a un menu che cresce di settimana
 * in settimana e non fa mai capire quanto manca.
 *
 * Ognuno vede le voci del suo livello di accesso, come nel v2: una voce si
 * chiama come la sua sezione, e i gruppi rimasti vuoti spariscono.
 */

interface Voce {
  a: string;
  icona: NomeIcona;
  testo: string;
  pronta?: boolean;
}

const GRUPPI: { titolo?: string; voci: Voce[] }[] = [
  { voci: [{ a: '/', icona: 'lavoro', testo: t('Il mio lavoro'), pronta: true }] },
  {
    titolo: t('Anagrafiche'),
    voci: [
      { a: '/customers', icona: 'clienti', testo: t('Clienti'), pronta: true },
      { a: '/suppliers', icona: 'fornitori', testo: t('Fornitori'), pronta: true },
      { a: '/agents', icona: 'agenti', testo: t('Agenti'), pronta: true },
      { a: '/leads', icona: 'lead', testo: t('Lead'), pronta: true },
      { a: '/prospects', icona: 'prospect', testo: t('Prospect'), pronta: true },
      { a: '/operators', icona: 'operatori', testo: t('Operatori'), pronta: true },
    ],
  },
  {
    titolo: t('Prodotti'),
    voci: [
      { a: '/items', icona: 'articoli', testo: t('Articoli'), pronta: true },
      { a: '/price-lists', icona: 'listini', testo: t('Listini'), pronta: true },
      { a: '/negotiations', icona: 'trattative', testo: t('Trattative'), pronta: true },
    ],
  },
  {
    titolo: t('Contabilità'),
    voci: [{ a: '/sales-orders', icona: 'ordini', testo: t('Ordini di vendita'), pronta: true }],
  },
  {
    // la produzione vede solo questa voce: gli ordini da confermare, senza
    // prezzi (Rita, 26 ottobre 2026)
    titolo: t('Produzione'),
    voci: [{ a: '/production', icona: 'produzione', testo: t('Da confermare'), pronta: true }],
  },
  {
    titolo: t('Documentazione'),
    voci: [{ a: '/documents', icona: 'documenti', testo: t('Documenti'), pronta: true }],
  },
  {
    titolo: t('Impostazioni'),
    voci: [
      { a: '/reference-data', icona: 'datiGenerici', testo: t('Dati generici'), pronta: true },
      { a: '/integrations', icona: 'integrazioni', testo: t('Integrazioni'), pronta: true },
      { a: '/settings', icona: 'parametri', testo: t('Parametri'), pronta: true },
      { a: '/profile', icona: 'profilo', testo: t('Profilo'), pronta: true },
    ],
  },
];

/** «Il mio lavoro» e il profilo sono di tutti; le altre voci sono sezioni. */
const DI_TUTTI = new Set(['/', '/profile']);

export function BarraLaterale({ onNaviga }: { onNaviga?: () => void }) {
  const percorso = useRouterState({ select: (s) => s.location.pathname });
  const uscita = useUscita();
  const puo = usePuoAprire();
  const gruppi = GRUPPI.map((g) => ({
    ...g,
    voci: g.voci.filter((v) => DI_TUTTI.has(v.a) || puo(v.a.slice(1) as Sezione)),
  })).filter((g) => g.voci.length > 0);

  return (
    <aside className="sidebar" id="menu-principale">
      {/* il logo è quello di San Marzano anche per la Inc, come all'accesso:
          l'azienda in uso la dice la barra in alto */}
      <div className="brand">
        <img
          className="brand-logo"
          src={marchio}
          alt="La Regina di San Marzano"
          width={640}
          height={317}
        />
        <span className="brand-sub">CRM 3.0</span>
      </div>
      <nav className="nav">
        {gruppi.map((gruppo, i) => (
          <div className="nav-group" key={gruppo.titolo ?? i}>
            {gruppo.titolo && <div className="nav-label">{t(gruppo.titolo)}</div>}
            {gruppo.voci.map((voce) => (
              <Link
                key={voce.a}
                to={voce.pronta ? voce.a : '/coming-soon'}
                search={voce.pronta ? undefined : { sezione: t(voce.testo) }}
                aria-current={percorso === voce.a ? 'page' : undefined}
                onClick={onNaviga}
              >
                <span className="nav-icon">
                  <Icona nome={voce.icona} />
                </span>{' '}
                {t(voce.testo)}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* Sul telefono «Esci» non sta nella barra in alto — lì ci stanno tre
          cose e basta — quindi vive qui in fondo al cassetto. */}
      <button
        className="nav-esci solo-stretto"
        onClick={() => uscita.mutate()}
        disabled={uscita.isPending}
      >
        {uscita.isPending ? t('Esco…') : t('Esci')}
      </button>
    </aside>
  );
}
